import { createContext, useContext } from 'react';
import { db } from '../lib/data';

/**
 * Exposes the data layer to the screens that reach for it through context.
 *
 * The previous version opened the application by querying the hospitals table
 * once to prove the connection worked, retrying with backoff, and then set
 * `isInitialized` to true whether or not it succeeded. That round trip
 * delayed every first paint and decided nothing, so it is gone; the flag stays
 * because consumers gate their first fetch on it.
 */
interface DataContextType {
  db: typeof db;
  isInitialized: boolean;
}

const DataContext = createContext<DataContextType | null>(null);

const value: DataContextType = { db, isInitialized: true };

export function DataProvider({ children }: { children: React.ReactNode }) {
  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useData() {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
}

/** True for the failure modes worth retrying: the network, not the request. */
export function isNetworkError(error: unknown): boolean {
  const e = error as { message?: string; code?: string } | null;
  return (
    !navigator.onLine ||
    e?.code === 'network_error' ||
    (e?.message?.includes('Failed to fetch') ?? false) ||
    (e?.message?.includes('NetworkError') ?? false)
  );
}

export async function retryWithBackoff<T>(
  operation: () => Promise<T>,
  maxRetries = 3,
  baseDelay = 1000,
): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      if (!isNetworkError(error) || attempt === maxRetries) throw error;
      await new Promise(resolve => setTimeout(resolve, baseDelay * 2 ** attempt));
    }
  }
  throw lastError;
}
